import {Head, router, usePage} from '@inertiajs/react'
import Layout from "../../components/layout";
import {CameraIcon} from "@heroicons/react/24/outline";
import {useCallback, useEffect, useRef, useState} from "react";
import toast from "react-hot-toast";
import 'react-tooltip/dist/react-tooltip.css'
import {Tooltip} from 'react-tooltip';
import 'react-responsive-modal/styles.css';
import DarkModal from '../../components/darkModal.jsx';
import AuthForm from '../../components/authForm.jsx';
import { captureException, metrics } from '@sentry/react';
import {useWebPush} from '../../hooks.js';
import {useTranslation} from 'react-i18next';
import {GALLERY_PLACEHOLDER_IMAGES} from '../../galleryPlaceholderImage.js';

function InstallPopup() {
    return <div className="text-white-foreground">
        <p className="text-center">Please install this webapp for a better experience</p>
        <p className="flex font-semibold justify-center flex-row items-center">Tap
            <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"
                 className="w-6 h-6 text-primary-400 mx-1">
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path
                    d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z"
                    fill="currentColor" />
            </svg>
            below and then&nbsp;<span className="text-primary-400">Add to Home Screen</span>
        </p>
    </div>
}

// Matches the app's LatestPhotos fan-of-3-cards (src/components/appclip/latestPhotos.tsx):
// newest photo front/largest/left, oldest back/smallest/right. Static here —
// the app's fly-in-from-camera entrance animation isn't worth chasing for a
// decorative gallery-button thumbnail.
const PHOTO_STACK_POSITIONS = [
    {x: -20, y: -2, rotate: -10, scale: 1, zIndex: 3},
    {x: 0, y: 0, rotate: 0, scale: 0.9, zIndex: 2},
    {x: 20, y: -1, rotate: 10, scale: 0.8, zIndex: 1},
];

function PhotoStack({captures}) {
    const cards = [...captures, ...GALLERY_PLACEHOLDER_IMAGES.slice(0, 3 - captures.length)];

    return (
        <div className="relative w-20 h-[60px] shrink-0">
            {PHOTO_STACK_POSITIONS.map((p, i) => (
                <img
                    key={i}
                    src={cards[i]}
                    alt=""
                    className="absolute top-1/2 left-1/2 w-10 h-[60px] rounded-[8px] object-cover"
                    style={{
                        zIndex: p.zIndex,
                        transform: `translate(-50%, -50%) translate(${p.x}px, ${p.y}px) rotate(${p.rotate}deg) scale(${p.scale})`,
                    }}
                />
            ))}
        </div>
    );
}

export default function CameraIndex({
                                        has_account, event: initialEvent, logoUrl, webPushUrl,
                                        photoMimes,
                                        videoMimes,
                                        videosEnabled,
                                        videoUploadUrl, uploadUrl,
                                        latestPhotos
                                    }) {
    const {auth} = usePage().props
    const tooltipRef = useRef(null);
    const [event, setEvent] = useState(initialEvent);
    const [tempImage, setTempImage] = useState(null);
    const [captures, setCaptures] = useState(latestPhotos ?? []);
    const [open, setOpen] = useState(false);
    const [openAuth, setOpenAuth] = useState(false);
    const {t} = useTranslation();
    const {request} = useWebPush(webPushUrl);

    const [currentType, setCurrentType] = useState('photo');

    const onCloseModal = useCallback(() => {
        setOpen(false);
    }, []);

    const onPhotosViewPressed = useCallback(() => {
        // if (event.reveal_type === 'hidden' && !event.is_organizer) {
        //     setOpen(true);
        //     return;
        // }
        router.get(`/camera/${event.id}/photos`)
    }, [event, has_account]);

    useEffect(() => {
        if (auth.user.name && !has_account && localStorage.getItem('camera_has_account_prompted') !== '1' && event.photos_taken > 0) {
            setOpenAuth(true);
            localStorage.setItem('camera_has_account_prompted', '1')
        }
    }, [has_account, auth.user, event.photos_taken])

    const onScreenshotPress = useCallback(async (takeEvent) => {

        console.log('onScreenshotPress');
        const shot = takeEvent.target.files[0];
        console.log(shot);
        takeEvent.target.value = null;
        if (event.photos_taken >= event.photo_limit && !event.unlimited_photos) {
            toast.error(t('You have no more photos left'));
            return;
        }

        if (!shot) {
            console.warn('shot is null');
            return;
        }

        const isVideo = shot.type.startsWith('video/');
        const url = isVideo ? videoUploadUrl : uploadUrl


        const imageUrl = URL.createObjectURL(shot);
        setTempImage(imageUrl);

        const formData = new FormData();
        formData.append('file', shot);

        if (isVideo) {
            formData.append('file_type', 'video');
            formData.append('width', 0);
            formData.append('height', 0);
            formData.append('duration', 0);
        } else {
            formData.append('file_type', 'photo');
            formData.append('generate_blurhash', true);
        }
        console.log(shot.name);
        formData.append('capture_id', shot.name);


        try {
            request();
            const requestStartedAt = new Date().getTime();
            await toast.promise(
                axios.post(url, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }),
                {
                    loading: t('Uploading photo...'),
                    success: function () {
                        metrics.gauge("upload_time", new Date().getTime() - requestStartedAt, {
                            tags: {type: 'camera'},
                            unit: "millisecond",
                        });

                        setEvent(e => ({
                            ...e,
                            photos_taken: e.photos_taken + 1
                        }))
                        // video blobs can't render through an <img>, so only photos
                        // update the gallery-button photo stack
                        if (!isVideo) {
                            setCaptures(prev => [imageUrl, ...prev].slice(0, 3));
                        }
                        return t('Uploaded ✅');
                    },
                    error: function (error) {
                        metrics.gauge("upload_time", new Date().getTime() - requestStartedAt, {
                            tags: {type: 'camera'},
                            unit: "millisecond",
                        });
                        if (error.response && error.response.status !== 400) {
                            captureException(error);
                            return t('Failed to upload. Do you have an internet connection?');
                        }
                        if (error.response && error.response.data) {
                            return error.response.data.message
                        }
                        console.warn(error);
                        return t('Failed to upload. Do you have an internet connection?');

                    }
                }
            )
            if (!auth.user.name) {
                router.reload()
            }
        } catch (e) {
            console.warn(e);
        } finally {
            setTempImage(null);
        }
    }, [event, auth.user]);

    useEffect(() => {
        const isIos = () => {
            const userAgent = window.navigator.userAgent.toLowerCase();
            return /iphone|ipad|ipod/.test(userAgent);
        }
        const isInStandaloneMode = () => ('standalone' in window.navigator) && (window.navigator.standalone);

        if (isIos() && !isInStandaloneMode() && !localStorage.getItem('popup_seen')) {
            setTimeout(() => {
                localStorage.setItem('popup_seen', true)
                tooltipRef.current?.open(
                    {
                        anchorSelect: '#tooltip-element',
                        place: 'top',
                    }
                )
                setTimeout(() => {
                    tooltipRef.current?.close();
                }, 10000);
            }, 100)
        }
    }, []);

    return (
        <Layout>
            <Head title={event.name + ' - Camera'} />
            <div
                className="flex flex-col px-[20px] grow justify-between h-full full-screen orient max-w-2xl mx-auto">
                {/*{event.reveal_type === 'hidden' && !event.is_organizer && <Modal open={open} onClose={onCloseModal} center>*/}
                {/*    {event.reveal_type === 'hidden' && <div>*/}
                {/*        <h2 className="font-bold text-xl">Album will not be revealed.</h2>*/}
                {/*        <p className="text-sm">The organizer of this event chose to <span*/}
                {/*            className="font-semibold">not</span> reveal the photos after the event. But all photos you*/}
                {/*            take will end up in the album for the organizer to enjoy after this event.</p>*/}
                {/*    </div>}*/}
                {/*</Modal>}*/}
                {(event.reveal_type === 'after' || event.reveal_type === 'during') && !has_account &&
                    <DarkModal open={openAuth} onClose={() => setOpenAuth(false)} center>
                        <AuthForm isAfter={event.reveal_type === 'after'} event={event} />
                    </DarkModal>}
                {/* fills the remaining space between the top and the bottom control
                    cluster, same as the app's live preview (flex: 1, no fixed
                    aspect-ratio box) instead of a boxed placeholder */}
                <div className="mt-[20px] flex-1 flex flex-col min-h-0">
                    <div className="relative flex-1 min-h-0 rounded-[50px] overflow-hidden bg-back border-[0.5px] border-[#2E2E2E]">
                        {tempImage && <img src={tempImage}
                                           className="absolute inset-0 w-full h-full object-cover" />}
                        <input onChange={onScreenshotPress} id="capture-camera" className="hidden opacity-0"
                               type="file"
                               name="file" accept={currentType === 'photo' ? photoMimes : videoMimes}
                               capture="environment" />
                        <label htmlFor="capture-camera"
                               className={(tempImage ? 'hidden ' : '') + 'absolute inset-0 flex flex-col items-center justify-center px-10'}>
                            <div
                                className="flex items-center justify-center w-24 h-24 mb-5"
                                style={{background: 'radial-gradient(circle, rgba(168,85,247,0.28), transparent 70%)'}}
                            >
                                <img className="size-16" src={logoUrl} alt="" />
                            </div>
                            <h3 className="text-primary-500 text-xl font-bold mb-2 text-center">{t('Tap here to open camera')}</h3>
                            <p className="text-white-foreground/70 text-center text-sm max-w-[240px]">{t('Tap here to open camera and take pictures for the album.')}</p>
                        </label>
                    </div>
                </div>
                {/* bottom control cluster — mirrors the app's bottom-anchored camera
                    controls (counter, then shutter row, then mode slider) instead of
                    the old top-bar layout */}
                <div className="mt-[20px] mb-[20px] flex flex-col items-center justify-center gap-4">
                    <div
                        className="flex flex-row items-center button-backdrop">
                        <CameraIcon className="w-[25px] mr-2 h-[25px] text-white-foreground" />
                        <div className="flex flex-row items-center">
                            <span
                                className="text-white-foreground text-lg font-bold">{event.unlimited_photos ? event.photos_taken : event.photo_limit - event.photos_taken} /</span>
                            {event.unlimited_photos ?
                                <svg className="text-white-foreground ml-1" xmlns="http://www.w3.org/2000/svg"
                                     width="32"
                                     height="32" fill="currentColor"
                                     viewBox="0 0 256 256">
                                    <path
                                        d="M252,128a60,60,0,0,1-102.43,42.43l-.49-.53L89.22,102.31a36,36,0,1,0,0,51.38l3.08-3.48a12,12,0,1,1,18,15.91l-3.35,3.78-.49.53a60,60,0,1,1,0-84.86l.49.53,59.86,67.59a36,36,0,1,0,0-51.38l-3.08,3.48a12,12,0,1,1-18-15.91l3.35-3.78.49-.53A60,60,0,0,1,252,128Z"></path>
                                </svg>
                                : <span
                                    className="text-white-foreground text-lg font-bold">&nbsp;{event.photo_limit}</span>}
                        </div>
                    </div>
                    {/* the shutter is absolutely centered over the row (like the app's
                        FlexRow position:absolute technique) so it stays centered
                        regardless of what's on either side — there's nothing on the
                        left (no QR sheet on web) and the photo stack on the right is
                        wider than a symmetric spacer would be */}
                    <div className="relative w-full" style={{height: 85}}>
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <label
                                htmlFor="capture-camera"
                                className="pointer-events-auto w-[85px] h-[85px] border-[7px] bg-white border-primary-500 rounded-full" />
                        </div>
                        <button
                            className="absolute right-[30px] top-1/2 -translate-y-1/2"
                            onClick={onPhotosViewPressed}>
                            <PhotoStack captures={captures} />
                        </button>
                    </div>
                    {videosEnabled && (
                        <div className="mode-slider-track">
                            <div className="mode-slider-thumb" style={{transform: currentType === 'video' ? 'translateX(84px)' : 'translateX(0)'}} />
                            <button className="mode-slider-segment" type="button" onClick={() => setCurrentType('photo')}>PHOTO</button>
                            <button className="mode-slider-segment" type="button" onClick={() => setCurrentType('video')}>VIDEO</button>
                        </div>
                    )}
                </div>
                <div id="tooltip-element">
                    <Tooltip style={{
                        backgroundColor: '#1f152d',
                        border: '0.5px solid #2E2E2E',
                        borderRadius: 16,
                    }} ref={tooltipRef}>
                        <InstallPopup />
                    </Tooltip>
                </div>
            </div>
        </Layout>
    )
}
